import { type ReactNode } from 'react';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dice5Icon } from 'lucide-react';
import { useI18n } from '@/providers/i18n';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { cn } from '@/lib/utils';
import { OptionRemark } from '@/components/nodes/shared/option-remark';

type UiOption = any;

type AdvancedOptionsPanelProps = {
  id: string;
  uiOptions: UiOption[];
  modelUi: Record<string, unknown> | undefined;
  canUseAdvanced: boolean;
  onChange: (key: string, value: unknown) => void;
  /** 每行显示的选项数量；不传或为 1 时按单列竖排渲染 */
  columns?: number;
};

type OptionCtx = {
  id: string;
  modelUi: any;
  isPreview: boolean;
  safeOnChange: (k: string, v: unknown) => void;
  t: (s: string) => string;
  prefix: string;
  /** 卡片布局：标题在左、控件在右、无边框透明控件 */
  card?: boolean;
};

// 仅渲染控件本身（不含外层布局）。card 模式下控件无边框/透明并撑满，
// 非 card 模式保持固定宽度 + 默认边框样式。两种布局共用此函数，避免重复。
function renderControl(opt: any, ctx: OptionCtx): ReactNode {
  const { id, modelUi, isPreview, safeOnChange, t, card } = ctx;
  const disabled = isPreview || opt.readonly;
  const inputCard = 'border-none bg-transparent dark:bg-transparent shadow-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0';

  switch (opt.type) {
    case 'switch':
      return (
        <Switch id={`${id}-${opt.key}`} checked={Boolean(modelUi?.[opt.key] ?? opt.default)} onCheckedChange={(val) => safeOnChange(opt.key, val)} disabled={disabled} />
      );
    case 'slider': {
      const val = typeof modelUi?.[opt.key] === 'number' ? modelUi[opt.key] : opt.default;
      return (
        <div className="flex items-center gap-2 w-full">
          <Slider id={`${id}-${opt.key}`} min={opt.min} max={opt.max} step={opt.step} value={[val]} onValueChange={(vals) => safeOnChange(opt.key, Number(vals[0]))} disabled={disabled} />
          <span className="text-xs w-8 text-right shrink-0 tabular-nums">{Number(val).toFixed(2)}</span>
        </div>
      );
    }
    case 'number':
      return (
        <div className={cn('flex items-center gap-1', card && 'w-full')}>
          <Input id={`${id}-${opt.key}`} type="number" className={cn('h-7 text-right text-xs', card ? `flex-1 min-w-0 ${inputCard}` : 'w-28')} value={typeof modelUi?.[opt.key] === 'number' ? modelUi[opt.key] : opt.default} onChange={(e) => safeOnChange(opt.key, Number((e.target as HTMLInputElement).value))} min={opt.min} max={opt.max} step={opt.step ?? 1} autoFocus={false} disabled={disabled} />
          {opt.key === 'seed' && !opt.readonly ? (
            <Button type="button" size="icon" variant="ghost" className="rounded-full h-7 w-7 shrink-0" title={t('Random seed')} disabled={isPreview} onClick={() => { if (isPreview) return; const lo = Number(opt.min ?? -Math.pow(256, 3)); const hi = Number(opt.max ?? Math.pow(256, 3)); safeOnChange(opt.key, Math.floor(Math.random() * (hi - lo + 1)) + lo); }}>
              <Dice5Icon size={11} />
            </Button>
          ) : null}
        </div>
      );
    case 'input':
      return (
        <Input id={`${id}-${opt.key}`} type="text" className={cn('h-7 text-xs', card ? `w-full text-right ${inputCard}` : 'w-32')} value={typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] : (opt.default ?? '')} onChange={(e) => safeOnChange(opt.key, String((e.target as HTMLInputElement).value))} disabled={disabled} readOnly={Boolean(opt.readonly)} />
      );
    case 'select':
      return (
        <Select value={typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] as string : opt.default} onValueChange={(val) => safeOnChange(opt.key, val)} disabled={disabled}>
          <SelectTrigger className={cn('h-7 text-xs', card ? 'w-full gap-1 border-none bg-transparent dark:bg-transparent dark:hover:bg-transparent shadow-none px-1 justify-end focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-50' : 'w-28')}><SelectValue /></SelectTrigger>
          <SelectContent className={card ? 'fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border border-sidebar-border rounded-xl shadow-none' : undefined}>{opt.options?.map((o: any) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
        </Select>
      );
    default:
      return null;
  }
}

function renderOption(opt: any, ctx: OptionCtx) {
  const { t, card, prefix } = ctx;
  const control = renderControl(opt, ctx);
  if (control == null) return null;

  const label = (
    <span className="inline-flex items-center gap-1 min-w-0 shrink-0">
      <span className="truncate">{t(opt.label)}</span>
      {opt.remark ? <OptionRemark text={t(opt.remark)} /> : null}
    </span>
  );

  // 卡片布局：圆角边框 + 标题在左、控件在右。
  if (card) {
    return (
      <div key={`${prefix}-${opt.key}`} className="flex items-center gap-2 rounded-xl border border-sidebar-border px-2.5 py-1.5 min-w-0 h-9">
        <span className="text-[11px] font-medium text-muted-foreground/80 min-w-0 shrink-0">{label}</span>
        <div className="flex-1 flex items-center justify-end min-w-0">{control}</div>
      </div>
    );
  }

  // 竖排布局（侧栏）：整行一项，标题在左、控件靠右。
  return (
    <div key={`${prefix}-${opt.key}`} className="h-9 flex items-center gap-2 w-full">
      <span className="inline-flex items-center h-5 text-xs shrink-0">{label}</span>
      <div className="flex-1 flex items-center justify-end min-w-0">{control}</div>
    </div>
  );
}

export function AdvancedOptionsPanel({ id, uiOptions, modelUi, canUseAdvanced, onChange, columns }: AdvancedOptionsPanelProps) {
  const { t } = useI18n();
  const isPreview = usePreviewCanvas();
  const safeOnChange = (key: string, value: unknown) => {
    if (isPreview) return;
    onChange(key, value);
  };
  const visible = uiOptions.filter((o: any) => o.group !== 'hidden');
  const basic = visible.filter((o: any) => o.group !== 'advanced' && o.group !== 'inline');
  const adv = visible.filter((o: any) => o.group === 'advanced');
  const showAdvancedOptions = Boolean(canUseAdvanced || isPreview);
  const useGrid = typeof columns === 'number' && columns > 1;
  const ctx = { id, modelUi: modelUi as any, isPreview, safeOnChange, t, card: useGrid };

  return (
    <div
      className={useGrid ? 'grid gap-2' : 'flex flex-col gap-1.5'}
      style={useGrid ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
    >
      {basic.map((opt: any) => renderOption(opt, { ...ctx, prefix: 'basic' }))}

      {adv.length ? (
        showAdvancedOptions ? (
          adv.map((opt: any) => renderOption(opt, { ...ctx, prefix: 'adv' }))
        ) : (
          <div className={`text-xs text-muted-foreground ${useGrid ? 'col-span-full' : ''}`}>
            {t('Advanced options require plan:')}
            <button
              type="button"
              className="ml-2 underline underline-offset-2 hover:opacity-80"
              onClick={() => { try { window.dispatchEvent(new Event('open_plans_pricing')); } catch {} }}
            >
              {t('Upgrade')}
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}